import React, { ReactNode, ReactElement, useState, useCallback, useRef, FC, useEffect } from "react"
import { WalkthroughContext } from 'react-native-walkthrough';
import Tooltip, { TooltipChildrenContext, TooltipProps } from 'react-native-walkthrough-tooltip';
import {
  Dimensions,
  LayoutRectangle,
  Platform,
  ScrollView,
  View as RNView,
  ViewStyle,
} from "react-native"
import { View } from "native-base"
import { color } from "../../theme"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { ColorType } from "native-base/lib/typescript/components/types"

type UseTooltipChildContext =
  | {
      useTooltipChildContext: true;
      children: (value: { tooltipDuplicate: boolean }) => ReactNode;
    }
  | { useTooltipChildContext?: false; children?: ReactNode };

type Props = {
  id: string;
  content?: ReactElement;
  tooltipProps?: TooltipProps & { parentWrapperStyle: ViewStyle };
  scrollViewRef?: { current: ScrollView };
  scrollOffset?: number;
  backgroundColor?: ColorType;
} & UseTooltipChildContext;

type DeferredTooltipProps = TooltipProps & {
  deferredTimeout?: number
}

function lazyTooltipHOC(WrappedComponent) {
  return function LazyTooltip(props) {
    const { isVisible, deferredTimeout = 1000 } = props
    const [ deferredIsVisible, setDeferredIsVisible ] = useState<boolean>(false)

    useEffect(() => {
      if (isVisible) {
        const timeoutId = setTimeout(() => {
          setDeferredIsVisible(true)
        }, deferredTimeout)
        return () => {
          clearTimeout(timeoutId)
        }
      }
      return setDeferredIsVisible(false)
    }, [deferredTimeout, isVisible])

    return <WrappedComponent {...props} isVisible={deferredIsVisible} />
  }
}

const LazyTooltip: FC<DeferredTooltipProps> = lazyTooltipHOC(Tooltip)

export const WalkthroughElement: FC<Props> = props => {
  const { scrollViewRef, scrollOffset = 0, backgroundColor = color.palette.white } = props
  const elementId = props.id;
  const [ layout, setLayout ] = useState<LayoutRectangle>(null)
  const insets = useSafeAreaInsets()
  const viewRef = useRef<RNView>()
  const onLayout = useCallback(() => {
    if (viewRef.current)
      viewRef.current.measureInWindow((x, y, width, height) => {
        setLayout({ x, y, width, height })
      })
  }, [])

  const defaultPlacement = React.Children.count(props.children) === 0 ? 'center' : 'top';

  return (
    <WalkthroughContext.Consumer>
      {({ currentElement, goToNext }) => {
        const defaultTooltipProps: TooltipProps = {
          useInteractionManager: true,
          isVisible: elementId === currentElement.id,
          content: props.content || currentElement.content,
          placement: currentElement.placement || defaultPlacement,
          onClose: () => {
            goToNext();
            currentElement.onClose?.();
          },
        };

        const tooltipProps: TooltipProps = {
          ...defaultTooltipProps,
          ...currentElement.tooltipProps,
          ...props.tooltipProps,
        };

        const tooltipPropsExtended: TooltipProps & { parentWrapperStyle?: ViewStyle } = {
          useInteractionManager: true,
          ...tooltipProps,
          backgroundStyle: {
            width: Dimensions.get('window').width,
            marginRight: 'auto',
            marginLeft: 'auto',
            paddingBottom: insets.bottom,
            ...(tooltipProps?.backgroundStyle as any),
          },
          tooltipStyle: {
            ...(Platform.OS === 'web' ? { shadowColor: "none" } : {}),
            alignItems: 'center',
            marginRight: 'auto',
            marginLeft: 'auto',
            width: undefined,
            ...(tooltipProps?.tooltipStyle as any),
          },
          contentStyle: {
            height: undefined,
            width: undefined,
            maxWidth: 320,
            ...(tooltipProps?.contentStyle as any),
          },
          arrowStyle: {
            left: undefined,
            ...(tooltipProps?.arrowStyle as any),
          }
        }

        if (tooltipPropsExtended.isVisible && scrollViewRef && scrollViewRef.current && layout)
          scrollViewRef.current.scrollTo({ y: layout.y - scrollOffset });

        return (
          <LazyTooltip {...tooltipPropsExtended} deferredTimeout={(!scrollViewRef || !scrollViewRef.current) ? 0 : 1000}>
            {props.useTooltipChildContext ? (
              <TooltipChildrenContext.Consumer>{props.children}</TooltipChildrenContext.Consumer>
            ) : (<TooltipChildrenContext.Consumer>
              {({ tooltipDuplicate }) => <View
                ref={!tooltipDuplicate && viewRef || undefined}
                onLayout={!tooltipDuplicate && onLayout || undefined}
                alignSelf="stretch"
                backgroundColor={tooltipDuplicate ? backgroundColor : "transparent"}
                style={tooltipPropsExtended.parentWrapperStyle}
              >
                {props.children}
              </View>}
            </TooltipChildrenContext.Consumer>)}
          </LazyTooltip>
        );
      }}
    </WalkthroughContext.Consumer>
  );
};

WalkthroughElement.defaultProps = {
  content: undefined,
  tooltipProps: undefined,
  useTooltipChildContext: false,
};
